import { ITEM_ROOT } from 'odata-query';
import { FilterOperatorType } from '../components/odata-filter/models/filter-operator-type';
import {
  FilterGroup,
  ODataFilterValues,
  ODataSubFilter
} from '../components/odata-filter/models/odata-filter';
import { FilterOperator, OPERATORS } from '../components/odata-filter/models/operators';
import { SearchValueType } from '../components/odata-filter/models/search-value-type';
import { ODataFilterBuilder } from './odata-filter-builder';
import {
  endOfLastMonth,
  endOfLastWeek,
  endOfMonth,
  endOfWeek,
  getDate,
  lastYear,
  startOfLastMonth,
  startOfLastWeek,
  startOfMonth,
  startOfWeek,
  thisYear,
  today,
  yesterday
} from '../utils/date-utils';

export class SurveyResponsesFilterModel {
  childFieldName = 'answers';
  targetFieldPrimaryKey = 'attributeDefinitionId';
  subFilter = new ODataSubFilter();
  constructor(
    public filterGroup: FilterGroup,
    public displayName: string,
    public targetField: string,
    public targetFieldId: string,
    public valueType: SearchValueType,
    public values?: any[] | ODataFilterValues[]
  ) {}

  buildFilter(searchValue: any, filterOperator: FilterOperator) {
    if (this.valueType == SearchValueType.String) {
      return this.buildStringTypeFilter(searchValue, filterOperator);
    } else if (this.valueType == SearchValueType.Number) {
      return this.buildNumberTypeFilter(searchValue, filterOperator);
    } else if (this.valueType == SearchValueType.MultiValue) {
      return this.buildEnumeratedTypeFilter(searchValue, filterOperator);
    } else if (this.valueType == SearchValueType.Date) {
      return this.buildDateTypeFilter(searchValue, filterOperator, false);
    } else {
      throw new Error('Invalid Operation');
    }
  }

  private buildStringTypeFilter(searchValue: any, filterOperator: FilterOperator) {
    if (searchValue) {
      searchValue = (searchValue as string).removeAccentMarks().toLowerCase().trim();
    }
    let operatorTypes = OPERATORS[this.valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    } else if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.targetField]: {
            any: ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId)
          }
        }
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      return {
        [this.targetField]: {
          any: ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId)
        }
      };
    } else {
      let filter = {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
            {
              [this.childFieldName]: {
                any: ODataFilterBuilder.buildStringFilter(
                  ITEM_ROOT,
                  searchValue,
                  filterOperator.actualOperator
                )
              }
            }
          ]
        }
      };

      if (filterOperator.displayOperator == FilterOperatorType.notContains) return { not: filter };
      return filter;
    }
  }

  private buildNumberTypeFilter(searchValue: any, filterOperator: FilterOperator) {
    let operatorTypes = OPERATORS[this.valueType];

    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }
    if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.targetField]: {
            any: ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId)
          }
        }
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      return {
        [this.targetField]: {
          any: ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId)
        }
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.notContains) {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
            {
              [this.childFieldName]: {
                any: ODataFilterBuilder.buildNumberFilter(
                  `to_double(${ITEM_ROOT})`,
                  searchValue,
                  FilterOperatorType.contains
                )
              }
            }
          ]
        }
      };
    } else {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
            {
              [this.childFieldName]: {
                any: ODataFilterBuilder.buildNumberFilter(
                  `to_double(${ITEM_ROOT})`,
                  searchValue,
                  filterOperator.actualOperator
                )
              }
            }
          ]
        }
      };
    }
  }

  private buildEnumeratedTypeFilter(
    searchValue: ODataFilterValues[],
    filterOperator: FilterOperator
  ) {
    let _searchValue = searchValue && searchValue.length ? searchValue.map((x) => x.name) : [];

    if (filterOperator.displayOperator == FilterOperatorType.in) {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
            {
              [this.childFieldName]: {
                any: ODataFilterBuilder.buildArrayFilter(ITEM_ROOT, _searchValue)
              }
            }
          ]
        }
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.notIn) {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
            {
              not: {
                [this.childFieldName]: {
                  any: ODataFilterBuilder.buildArrayFilter(ITEM_ROOT, _searchValue)
                }
              }
            }
          ]
        }
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.targetField]: {
            any: ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId)
          }
        }
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      return {
        [this.targetField]: {
          any: ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId)
        }
      };
    } else throw new Error('Invalid Operation');
  }

  buildDateTypeFilter(
    searchValue: any,
    filterOperator: FilterOperator,
    buildForAutomation: boolean
  ) {
    searchValue = getDate(searchValue);
    let operatorTypes = OPERATORS[this.valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    } else {
      if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
        return {
          not: {
            [this.targetField]: {
              any: ODataFilterBuilder.buildGuidFilter(
                this.targetFieldPrimaryKey,
                this.targetFieldId
              )
            }
          }
        };
      }

      if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
        return {
          [this.targetField]: {
            any: ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId)
          }
        };
      }
      if (
        filterOperator.displayOperator == FilterOperatorType.eq ||
        filterOperator.displayOperator == FilterOperatorType.lt ||
        filterOperator.displayOperator == FilterOperatorType.gt
      ) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: ODataFilterBuilder.buildSimpleFilter(
                    ITEM_ROOT,
                    searchValue,
                    filterOperator.displayOperator
                  )
                }
              }
            ]
          }
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.today) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: ODataFilterBuilder.buildSimpleFilter(ITEM_ROOT, today(buildForAutomation))
                }
              }
            ]
          }
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.yesterday) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: ODataFilterBuilder.buildSimpleFilter(
                    ITEM_ROOT,
                    yesterday(buildForAutomation)
                  )
                }
              }
            ]
          }
        };
      }
      if (
        filterOperator.displayOperator == FilterOperatorType.onOrBeforeToday ||
        filterOperator.displayOperator == FilterOperatorType.onOrAfterToday
      ) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: ODataFilterBuilder.buildSimpleFilter(
                    ITEM_ROOT,
                    today(buildForAutomation),
                    filterOperator.actualOperator
                  )
                }
              }
            ]
          }
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.thisYear) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: ODataFilterBuilder.buildSimpleFilter(
                    ITEM_ROOT,
                    thisYear(buildForAutomation),
                    FilterOperatorType.startswith
                  )
                }
              }
            ]
          }
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.lastYear) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: ODataFilterBuilder.buildSimpleFilter(
                    ITEM_ROOT,
                    lastYear(buildForAutomation),
                    FilterOperatorType.startswith
                  )
                }
              }
            ]
          }
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.thisWeek) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: {
                    and: [
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        startOfWeek(buildForAutomation),
                        FilterOperatorType.ge
                      ),
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        endOfWeek(buildForAutomation),
                        FilterOperatorType.le
                      )
                    ]
                  }
                }
              }
            ]
          }
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.lastWeek) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: {
                    and: [
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        startOfLastWeek(buildForAutomation),
                        FilterOperatorType.ge
                      ),
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        endOfLastWeek(buildForAutomation),
                        FilterOperatorType.le
                      )
                    ]
                  }
                }
              }
            ]
          }
        };
      }

      if (filterOperator.displayOperator == FilterOperatorType.thisMonth) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: {
                    and: [
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        startOfMonth(buildForAutomation),
                        FilterOperatorType.ge
                      ),
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        endOfMonth(buildForAutomation),
                        FilterOperatorType.le
                      )
                    ]
                  }
                }
              }
            ]
          }
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.lastMonth) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter(this.targetFieldPrimaryKey, this.targetFieldId),
              {
                [this.childFieldName]: {
                  any: {
                    and: [
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        startOfLastMonth(new Date(), buildForAutomation),
                        FilterOperatorType.ge
                      ),
                      ODataFilterBuilder.buildSimpleFilter(
                        ITEM_ROOT,
                        endOfLastMonth(new Date(), buildForAutomation),
                        FilterOperatorType.le
                      )
                    ]
                  }
                }
              }
            ]
          }
        };
      }
    }
  }
}
